// LandingPage.js
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { SG_GET_TASK_SCHEDULES, SG_GET_TASK_TEMPLATES } from "constants/actions";

// Context
import { usePulseSurveys, PulseSurveysProvider } from "./Data/LandingPageContext";

// Layout containers (no new_components)
const PageContainer = styled.div`
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 28px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const PageSubtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const SearchInput = styled.input`
  width: 55%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 13px;
  color: #414141;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #476dfa;
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 0.7fr 0.8fr 0.9fr;
  gap: 0px;
  padding: 12px 14px;
`;

const HeaderCell = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 0.7fr 0.8fr 0.9fr;
  gap: 0px;
  padding: 14px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  animation: slideInRow 0.3s ease-out;
  animation-fill-mode: both;

  &:hover {
    background: #f7f9ff;
  }

  @keyframes slideInRow {
    from {
      opacity: 0;
      transform: translateX(-12px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${(props) =>
    typeof props.index === "number" && props.index >= 0
      ? `animation-delay: ${props.index * 0.04}s;`
      : ""}
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  ${(props) => {
    switch (props.status) {
      case 1: // Draft
        return `background: #f0f4ff; color: #476dfa;`;
      case 3: // Sent
        return `background: #e8f5e8; color: #2e7d2e;`;
      case 51: // Closed
        return `background: #e3f2fd; color: #1976d2;`;
      default:
        return `background: #f0f0f0; color: #666;`;
    }
  }}
`;

const MutedText = styled.span`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ActionBtn = styled.button`
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 7px;
  font-family: "Poppins";
  font-size: 12px;
  color: #414141;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    color: #476dfa;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 0 0 0;
`;

const PageBtn = styled.button`
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
  color: #414141;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: #476dfa;
    color: #476dfa;
  }
`;

const ITEMS_PER_PAGE = 9;

const LandingPageContent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { surveyData, searchValue, handleSearch } = usePulseSurveys();

  const [currentPage, setCurrentPage] = useState(1);

  // Always refresh schedules/templates when landing mounts to reflect latest send
  useEffect(() => {
    dispatch({ type: SG_GET_TASK_SCHEDULES });
    dispatch({ type: SG_GET_TASK_TEMPLATES });
  }, [dispatch]);

  // Hide Draft status entries (statusValue === 1)
  const filteredData = useMemo(
    () => (surveyData || []).filter((row) => row?.statusValue !== 1),
    [surveyData]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((filteredData.length || 0) / ITEMS_PER_PAGE)),
    [filteredData.length]
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pageRows = useMemo(
    () => filteredData.slice(startIndex, endIndex),
    [filteredData, startIndex, endIndex]
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusLabel = (statusValue) => {
    switch (statusValue) {
      case 1:
        return "Draft";
      case 3:
        return "Sent";
      case 51:
        return "Closed";
      default:
        return "Unknown";
    }
  };

  const handleManageRecipients = (row) => {
    // For drafts, route to edit; for sent/closed, route to view
    if (row.statusValue === 1) {
      navigate(`/app/otp/AI/pulse-survey/edit/${row.id}`);
    } else {
      navigate(`/app/otp/AI/pulse-survey/view/${row.id}`);
    }
  };

  const handleCompare = (row) => {
    const selectedPulseSurvey = {
      id: row?.id,
      name: row?.name,
      statusValue: row?.statusValue,
      updatedValue: row?.updatedValue,
    };
    navigate("/app/otp/otp-comparison/step2", {
      state: { selectedPulseSurvey },
    });
  };

  const handleAnalyze = (row) => {
    navigate(`/app/otp/AI/pulse-survey/analytics/${row.id}`);
  };

  return (
    <PageContainer>
      <HeaderSection>
        <TitleBlock>
          <PageTitle>Pulse Surveys</PageTitle>
          <PageSubtitle>Manage recipients and explore survey results</PageSubtitle>
        </TitleBlock>
        <HeaderActions>
          <PrimaryButton onClick={() => navigate("/app/otp/AI/pulse-survey/survey-library")}>
            Create new
          </PrimaryButton>
        </HeaderActions>
      </HeaderSection>

      <Toolbar>
        <SearchInput
          placeholder="Search"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <MutedText>{filteredData.length || 0} total</MutedText>
      </Toolbar>

      <TableCard>
        <TableHeader>
          <TableHeaderRow>
            <HeaderCell>Survey</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Updated</HeaderCell>
            <HeaderCell>Actions</HeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {pageRows.map((row) => (
            <TableRow key={row.id}>
              <Cell>
                <NameText>{row.name}</NameText>
              </Cell>
              <Cell>
                <StatusBadge status={row.statusValue}>{getStatusLabel(row.statusValue)}</StatusBadge>
              </Cell>
              <Cell>
                <MutedText>{formatDate(row.updatedValue)}</MutedText>
              </Cell>
              <Cell>
                <RowActions>
                  <ActionBtn onClick={() => handleManageRecipients(row)}>Manage</ActionBtn>
                  <ActionBtn onClick={() => handleAnalyze(row)}>
                    Analyze
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => handleCompare(row)}
                    disabled={row?.statusValue === 1}
                    title={row?.statusValue === 1 ? "Compare is available only for Sent/Closed pulses" : "Compare"}
                  >
                    Compare
                  </ActionBtn>
                </RowActions>
              </Cell>
            </TableRow>
          ))}
          {pageRows.length === 0 && (
            <TableRow>
              <Cell>
                <MutedText>No surveys found.</MutedText>
              </Cell>
              <Cell />
              <Cell />
              <Cell />
            </TableRow>
          )}
        </TableBody>
      </TableCard>

      <PaginationBar>
        <MutedText>
          Page {currentPage} of {totalPages}
        </MutedText>
        <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
          Prev
        </PageBtn>
        <PageBtn
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </PageBtn>
      </PaginationBar>
    </PageContainer>
  );
};

const LandingPage = () => (
  <PulseSurveysProvider>
    <LandingPageContent />
  </PulseSurveysProvider>
);

export default LandingPage;
